import React, { useEffect, useRef, useState } from "react";
import api, { formatDate, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import EmployeePicker from "@/components/app/EmployeePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, FileSignature, Trash2, Plus, ScanText, AlertTriangle, FileDown, Pencil } from "lucide-react";

const STAGE_META = {
    expired: { label: "EXPIRED", cls: "bg-red-600 text-white" },
    "1_month": { label: "≤ 1 month", cls: "bg-red-100 text-red-700 border border-red-300" },
    "3_months": { label: "≤ 3 months", cls: "bg-orange-100 text-orange-700 border border-orange-300" },
    "6_months": { label: "≤ 6 months", cls: "bg-amber-100 text-amber-800 border border-amber-300" },
};

export default function Contracts() {
    const [data, setData] = useState(null);
    const [alerts, setAlerts] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [upOpen, setUpOpen] = useState(false);
    const [upEmployee, setUpEmployee] = useState("");
    const [upTemplate, setUpTemplate] = useState("");
    const [uploading, setUploading] = useState(false);
    const [tplForm, setTplForm] = useState({ name: "", contract_type: "contractual", body: "" });
    const [tplOpen, setTplOpen] = useState(false);
    const [editing, setEditing] = useState(null); // contract being corrected
    const [viewing, setViewing] = useState(null); // contract shown in the viewer modal
    const fileRef = useRef(null);

    const openViewer = async (c) => {
        setViewing({ ...c, __loading: true });
        try {
            const { data: full } = await api.get(`/contracts/${c.id}`);
            setViewing(full);
        } catch { setViewing(c); }
    };
    const fileKind = (name) => {
        const ext = (name || "").split(".").pop().toLowerCase();
        if (ext === "pdf") return "pdf";
        if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return "image";
        return "text";
    };

    const load = () => {
        api.get("/contracts").then((r) => setData(r.data)).catch(() => {});
        api.get("/contracts/alerts").then((r) => setAlerts(r.data)).catch(() => {});
        api.get("/contracts/templates").then((r) => setTemplates(r.data.items || [])).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    const upload = async () => {
        const f = fileRef.current?.files?.[0];
        if (!f) return toast.error("Pick the scanned contract file");
        setUploading(true);
        const fd = new FormData();
        fd.append("file", f);
        if (upEmployee) fd.append("employee_id", upEmployee);
        if (upTemplate) fd.append("template_id", upTemplate);
        try {
            const { data: c } = await api.post("/contracts", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const who = c.employee_name || c.parsed?.employee_guess?.employee_name;
            toast.success(`OCR read the contract${c.expiry_date ? ` — expires ${c.expiry_date}` : ""}${who ? ` · for ${who}` : ""}`, { duration: 6000 });
            if (!c.expiry_date || !c.employee_id) {
                toast.warning("Couldn't detect everything — open the contract row to fill in the blanks.");
            }
            setUpOpen(false); setUpEmployee(""); setUpTemplate("");
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Upload failed"); }
        setUploading(false);
    };

    const saveTemplate = async () => {
        try {
            await api.post("/contracts/templates", tplForm);
            toast.success("Template added");
            setTplOpen(false); setTplForm({ name: "", contract_type: "contractual", body: "" });
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const saveEdit = async () => {
        try {
            await api.put(`/contracts/${editing.id}`, {
                expiry_date: editing.expiry_date, start_date: editing.start_date,
                employee_id: editing.employee_id || undefined, notes: editing.notes,
            });
            toast.success("Contract updated");
            setEditing(null); load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const del = async (c) => {
        if (!window.confirm(`Delete the contract file "${c.filename}"${c.employee_name ? ` for ${c.employee_name}` : ""}?`)) return;
        try { await api.delete(`/contracts/${c.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div>
            <PageHeader title="Contracts" subtitle="Templates + scanned contracts for contractual, project-based, and agency employees. OCR reads the expiry and who it's for; HR is alerted 6 / 3 / 1 months before expiry."
                actions={
                    <div className="flex items-center gap-2">
                        <button className="h-9 px-3 rounded-md border border-border text-sm font-medium flex items-center gap-2" onClick={() => setTplOpen(true)} data-testid="add-template-btn"><Plus className="size-4" /> Template</button>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" onClick={() => setUpOpen(true)} data-testid="upload-contract-btn"><Upload className="size-4" /> Upload Contract</button>
                    </div>
                } />

            {alerts && alerts.total > 0 && (
                <Surface className="p-3 mb-3 border-amber-300 bg-amber-50/60" data-testid="contract-alerts">
                    <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 mb-2"><AlertTriangle className="size-4" /> {alerts.total} contract{alerts.total > 1 ? "s" : ""} need attention</div>
                    <div className="grid gap-1.5 md:grid-cols-2">
                        {["expired", "1_month", "3_months", "6_months"].flatMap((stage) =>
                            (alerts[stage] || []).map((a) => (
                                <div key={a.id} className="flex items-center justify-between rounded-md bg-white border border-border px-2.5 py-1.5 text-xs">
                                    <span><b>{a.employee_name || "Unassigned"}</b> · {a.contract_type} · expires <span className="font-mono">{a.expiry_date}</span></span>
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STAGE_META[stage].cls}`}>
                                        {stage === "expired" ? `expired ${Math.abs(a.days_to_expiry)}d ago` : `${a.days_to_expiry}d left`}
                                    </span>
                                </div>
                            )))}
                    </div>
                </Surface>
            )}

            <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
                <div>
                    <h3 className="font-display font-semibold mb-2 flex items-center gap-2"><ScanText className="size-4 text-primary" /> Contracts on file</h3>
                    {data && !data.items.length && <Surface className="p-5"><Empty title="No contracts yet" subtitle="Upload a scanned contract — OCR fills in the details." /></Surface>}
                    {data?.items.length > 0 && (
                        <TableWrap>
                            <thead><tr><th>Employee</th><th>Type</th><th>File</th><th>Start</th><th>Expiry</th><th>Alert</th><th>OCR</th><th></th></tr></thead>
                            <tbody>
                                {data.items.map((c) => (
                                    <tr key={c.id} data-testid={`contract-${c.id}`}>
                                        <td>{c.employee_name ? <b>{c.employee_name}</b> : <span className="text-destructive text-xs">unassigned</span>}</td>
                                        <td className="text-xs capitalize">{c.contract_type}</td>
                                        <td className="text-xs">
                                            <button className="text-primary underline inline-flex items-center gap-1" onClick={() => openViewer(c)} data-testid={`view-contract-${c.id}`}><ScanText className="size-3" />{c.filename}</button>
                                        </td>
                                        <td className="font-mono text-xs">{c.start_date || "—"}</td>
                                        <td className="font-mono text-xs">{c.expiry_date || <span className="text-destructive">unknown</span>}</td>
                                        <td>
                                            {c.alert_stage
                                                ? <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STAGE_META[c.alert_stage].cls}`}>{STAGE_META[c.alert_stage].label}{c.days_to_expiry != null && c.alert_stage !== "expired" ? ` · ${c.days_to_expiry}d` : ""}</span>
                                                : c.days_to_expiry != null ? <span className="text-[10px] text-muted-foreground">{c.days_to_expiry}d left</span> : "—"}
                                        </td>
                                        <td className="text-[10px] text-muted-foreground">{c.parsed?.extraction_method?.replace(/_/g, " ")}</td>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <button className="text-primary" title="Correct details" onClick={() => setEditing({ ...c })} data-testid={`edit-contract-${c.id}`}><Pencil className="size-3.5" /></button>
                                                <button className="text-muted-foreground hover:text-destructive" onClick={() => del(c)}><Trash2 className="size-3.5" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </TableWrap>
                    )}
                </div>

                <div>
                    <h3 className="font-display font-semibold mb-2 flex items-center gap-2"><FileSignature className="size-4 text-primary" /> Templates</h3>
                    <div className="space-y-1.5">
                        {templates.map((t) => (
                            <Surface key={t.id} className="p-2.5 flex items-center justify-between">
                                <div>
                                    <div className="text-sm font-medium">{t.name}</div>
                                    <div className="text-[10px] text-muted-foreground capitalize">{t.contract_type}</div>
                                </div>
                                <button className="text-muted-foreground hover:text-destructive" onClick={() => window.confirm(`Delete template "${t.name}"?`) && api.delete(`/contracts/templates/${t.id}`).then(load)}><Trash2 className="size-3.5" /></button>
                            </Surface>
                        ))}
                        {!templates.length && <Surface className="p-4"><Empty title="No templates" /></Surface>}
                    </div>
                </div>
            </div>

            {/* Contract viewer modal */}
            <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 flex-wrap">
                            <FileSignature className="size-4 text-primary" />
                            {viewing?.employee_name || "Unassigned"} — <span className="capitalize">{viewing?.contract_type}</span> contract
                            {viewing?.alert_stage && <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STAGE_META[viewing.alert_stage].cls}`}>{STAGE_META[viewing.alert_stage].label}</span>}
                        </DialogTitle>
                    </DialogHeader>
                    {viewing && (
                        <div className="space-y-2">
                            <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                                <span>Start: <b className="font-mono">{viewing.start_date || "—"}</b></span>
                                <span>Expiry: <b className="font-mono">{viewing.expiry_date || "unknown"}</b></span>
                                {viewing.days_to_expiry != null && <span>{viewing.days_to_expiry >= 0 ? `${viewing.days_to_expiry} days left` : `expired ${Math.abs(viewing.days_to_expiry)} days ago`}</span>}
                                <span>OCR: {viewing.parsed?.extraction_method?.replace(/_/g, " ") || "—"}</span>
                            </div>
                            <div className="border border-border rounded-md bg-slate-50 max-h-[55vh] overflow-auto" data-testid="contract-viewer-body">
                                {viewing.__loading ? (
                                    <div className="p-6 text-sm text-muted-foreground">Loading document…</div>
                                ) : fileKind(viewing.filename) === "pdf" ? (
                                    <iframe title="contract" src={`/api/contracts/${viewing.id}/file`} className="w-full h-[55vh]" />
                                ) : fileKind(viewing.filename) === "image" ? (
                                    <img alt="contract scan" src={`/api/contracts/${viewing.id}/file`} className="max-w-full mx-auto" />
                                ) : (
                                    <pre className="p-4 text-xs whitespace-pre-wrap font-mono">{viewing.parsed?.text || "No text extracted."}</pre>
                                )}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <a className="h-9 px-3 rounded-md border border-border text-sm flex items-center gap-1" href={viewing ? `/api/contracts/${viewing.id}/file` : "#"} download={viewing?.filename}><FileDown className="size-4" /> Download</a>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={() => { setEditing({ ...viewing }); setViewing(null); }}>Correct details</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Upload dialog */}
            <Dialog open={upOpen} onOpenChange={setUpOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Upload scanned contract</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        <div>
                            <Label>Scanned copy (PDF / image / DOCX)</Label>
                            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.docx,.txt" className="w-full text-sm border border-border rounded-md p-2" data-testid="contract-file" />
                        </div>
                        <div>
                            <Label>Template (optional — sets contract type)</Label>
                            <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={upTemplate} onChange={(e) => setUpTemplate(e.target.value)} data-testid="contract-template">
                                <option value="">— detect from document —</option>
                                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>Employee (optional — OCR will try to match)</Label>
                            <EmployeePicker value={upEmployee} onChange={setUpEmployee} testId="contract-employee" />
                        </div>
                        <div className="text-xs text-muted-foreground">OCR (AWS Textract with PDF/DOCX fallback) reads the document, detects the <b>expiry date</b> and <b>who the contract is for</b>. You can correct anything afterwards.</div>
                    </div>
                    <DialogFooter>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={upload} disabled={uploading} data-testid="contract-upload-submit">{uploading ? "Reading document…" : "Upload & scan"}</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Template dialog */}
            <Dialog open={tplOpen} onOpenChange={setTplOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>New contract template</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        <div><Label>Name</Label><Input value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} placeholder="Fixed-Term Employment Contract" data-testid="tpl-name" /></div>
                        <div>
                            <Label>Contract type</Label>
                            <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={tplForm.contract_type} onChange={(e) => setTplForm({ ...tplForm, contract_type: e.target.value })}>
                                {["contractual", "project", "agency", "probationary", "fixed-term"].map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div><Label>Template body / clauses</Label>
                            <textarea className="w-full h-32 rounded-md border border-border bg-background p-2 text-sm" value={tplForm.body} onChange={(e) => setTplForm({ ...tplForm, body: e.target.value })} placeholder="This Employment Contract is entered into between {company} and {employee}…" /></div>
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={saveTemplate} data-testid="tpl-save">Save template</button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Correct-details dialog */}
            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Correct contract details</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-2">
                            <div><Label>Employee</Label><EmployeePicker value={editing.employee_id || ""} onChange={(v) => setEditing({ ...editing, employee_id: v })} testId="edit-employee" /></div>
                            <div className="grid grid-cols-2 gap-2">
                                <div><Label>Start date</Label><Input type="date" value={editing.start_date || ""} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} /></div>
                                <div><Label>Expiry date</Label><Input type="date" value={editing.expiry_date || ""} onChange={(e) => setEditing({ ...editing, expiry_date: e.target.value })} data-testid="edit-expiry" /></div>
                            </div>
                            <div><Label>Notes</Label><Input value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
                            {editing.parsed?.dates_found?.length > 0 && <div className="text-[11px] text-muted-foreground">Dates the OCR found: {editing.parsed.dates_found.join(", ")}</div>}
                        </div>
                    )}
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={saveEdit} data-testid="edit-save">Save</button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
